import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';

import QaItemFormDialog, { QaItemFormValue } from '../components/qa/QaItemFormDialog';
import { QaTemplateItem, QaTemplateItemInput, SiteQaService } from '../services/SiteQaService';
import usePermissions from '../hooks/usePermissions';

/**
 * 공통 QA 리스트 — 전 현장이 공통으로 확인해야 할 QA 항목.
 * 라우트: /qa-template (현장 목록 [QA 리스트] 버튼)
 *
 * 조회는 로그인 사용자 누구나, 추가·수정·삭제는 admin (백엔드 RequireRole 과 같다).
 * 현장은 현장 페이지의 QA 메뉴에서 [공통 QA 가져오기] 로 이 목록을 복사해 진행한다.
 */
export default function QaTemplatePage() {
  const navigate = useNavigate();
  const { canAdmin } = usePermissions();
  const [items, setItems] = useState<QaTemplateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ open: boolean; editing: QaTemplateItem | null }>({
    open: false,
    editing: null,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await SiteQaService.listTemplate());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const initial = useMemo<Partial<QaItemFormValue> | undefined>(() => {
    const t = dialog.editing;
    if (!t) return undefined;
    return { category: t.category, title: t.title, method: t.method, assignee: t.assignee };
  }, [dialog.editing]);

  const submit = async (value: QaTemplateItemInput) => {
    if (dialog.editing) {
      await SiteQaService.updateTemplate(dialog.editing.id, value);
    } else {
      await SiteQaService.createTemplate(value);
    }
    await load();
  };

  const remove = async (t: QaTemplateItem) => {
    if (!window.confirm(`'${t.title}' 항목을 공통 QA 리스트에서 삭제할까요?\n이미 가져간 현장의 QA 기록은 남습니다.`)) return;
    try {
      await SiteQaService.deleteTemplate(t.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Paper
        elevation={0}
        square
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <IconButton size="small" onClick={() => navigate('/projects')}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate('/projects')}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <HomeIcon sx={{ fontSize: 16 }} />
            현장 목록
          </Link>
          <Typography color="text.primary" fontWeight={600}>
            QA 리스트
          </Typography>
        </Breadcrumbs>
      </Paper>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              공통 QA 리스트
            </Typography>
            <Typography variant="body2" color="text.secondary">
              모든 현장이 공통으로 확인할 QA 항목입니다. 현장 페이지의 QA 메뉴에서 [공통 QA 가져오기]로
              가져가며, 담당자는 가져갈 때 기본값으로 들어갑니다. 여기서 고치거나 지워도 이미 진행한 현장
              기록은 바뀌지 않습니다.
            </Typography>
          </Box>
          {canAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disableElevation
              onClick={() => setDialog({ open: true, editing: null })}
              data-testid="qa-template-add"
              sx={{ flexShrink: 0, textTransform: 'none', fontWeight: 600 }}
            >
              항목 추가
            </Button>
          )}
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : items.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            등록된 공통 QA 항목이 없습니다.{canAdmin ? ' [항목 추가]로 만들어 주세요.' : ''}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" data-testid="qa-template-table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 120 }}>분류</TableCell>
                  <TableCell>QA 항목</TableCell>
                  <TableCell>확인 방법 · 합격 기준</TableCell>
                  <TableCell sx={{ width: 110 }}>담당자</TableCell>
                  {canAdmin && <TableCell sx={{ width: 96 }} align="right" />}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell>{t.category ? <Chip size="small" label={t.category} /> : '—'}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t.title}</TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>{t.method || '—'}</TableCell>
                    <TableCell>{t.assignee || '—'}</TableCell>
                    {canAdmin && (
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="수정">
                          <IconButton size="small" onClick={() => setDialog({ open: true, editing: t })}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="삭제">
                          <IconButton size="small" onClick={() => remove(t)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      <QaItemFormDialog
        open={dialog.open}
        title={dialog.editing ? '공통 QA 항목 수정' : '공통 QA 항목 추가'}
        initial={initial}
        onClose={() => setDialog({ open: false, editing: null })}
        onSubmit={submit}
      />
    </Box>
  );
}
